import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Navbar.css';
import logo from '../Gallery/logo.png';
import { supabase } from '../lib/supabase';

/* ─── Component ─────────────────────────────────────────── */
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isMobileHolidaysOpen, setIsMobileHolidaysOpen] = useState(false);
  const [megaMenuData, setMegaMenuData] = useState([]);
  const megaRef = useRef(null);
  const navigate = useNavigate();

  // Fetch mega-menu: regions from navbar_config + destinations from itineraries table
  useEffect(() => {
    const fetchMenu = async () => {
      // Clear old cache so fresh data always loads
      localStorage.removeItem('navbar_config');

      // Fetch regions and active itineraries in parallel
      const [{ data: regionsData }, { data: itinsData }] = await Promise.all([
        supabase.from('navbar_config').select('region, color, fa_icon, active, display_order').eq('active', true).order('display_order'),
        supabase.from('itineraries').select('destination, region, slug, active').eq('active', true).order('destination'),
      ]);

      if (regionsData && regionsData.length > 0) {
        // Merge: each region gets its destinations from the itineraries table
        const merged = regionsData.map(r => ({
          ...r,
          destinations: (itinsData || [])
            .filter(i => i.region === r.region)
            .map(i => i.destination),
        }));
        setMegaMenuData(merged);
      }
    };
    fetchMenu();
  }, []);


  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setIsMegaOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* Navigate to itinerary page */
  const openItinerary = (label) => {
    setIsMegaOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileHolidaysOpen(false);
    navigate(`/itinerary/${encodeURIComponent(label)}`);
  };

  const navItems = [
    { name: 'Home', href: '/', icon: 'fa-solid fa-house' },
    { name: 'Holidays', href: '#holidays', icon: 'fa-solid fa-umbrella-beach', hasMega: true },
    { name: 'Short Break', href: '/shortbreak', icon: 'fa-solid fa-calendar-days', isRoute: true },
    { name: 'Flights', href: '/flights', icon: 'fa-solid fa-plane', isRoute: true },
    { name: 'Visa', href: '/visa', icon: 'fa-solid fa-passport', isRoute: true },
    { name: 'About Us', href: '/about', icon: 'fa-solid fa-circle-info', isRoute: true },
    { name: 'Contact Us', href: '/contact', icon: 'fa-solid fa-phone', isRoute: true },
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">

        {/* Logo */}
        <div className="navbar-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Silver Sky Holidays Logo" className="logo-img" />
          <div className="logo-text">
            <span className="logo-main">Silver Sky Holidays</span>
            <span className="logo-tagline">RELATIONSHIP OF TRUST</span>
            <div className="tagline-line"></div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <ul className="navbar-menu">
          {navItems.map((item, index) =>
            item.hasMega ? (
              <li key={index} className="navbar-item mega-parent" ref={megaRef}>
                <button
                  className={`navbar-link mega-trigger ${isMegaOpen ? 'mega-active' : ''}`}
                  onClick={() => setIsMegaOpen(!isMegaOpen)}
                >
                  <i className={`${item.icon} nav-icon`}></i>
                  <span className="nav-text">{item.name}</span>
                  <i className={`fa-solid fa-chevron-down mega-chevron ${isMegaOpen ? 'rotated' : ''}`}></i>
                </button>

                {/* ── MEGA DROPDOWN ── */}
                <div className={`mega-dropdown ${isMegaOpen ? 'mega-open' : ''}`}>
                  <div className="mega-inner">

                    {/* Banner */}
                    <div className="mega-banner">
                      <div className="mega-banner-text">
                        <span className="mega-banner-title"><i className="fa-solid fa-plane-departure"></i> Explore the World</span>
                        <span className="mega-banner-sub">250+ handcrafted holiday packages — click any destination to view the full itinerary</span>
                      </div>
                      <button className="mega-banner-btn" onClick={() => { setIsMegaOpen(false); navigate('/packages'); }}>
                        View All Packages
                      </button>
                    </div>

                    {/* Region Grid */}
                    <div className="mega-grid">
                      {megaMenuData.length === 0 ? (
                        <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: 13 }}>
                          <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
                          Loading destinations...
                        </div>
                      ) : megaMenuData.map((region) => (
                        <div className="mega-region" key={region.region}>
                          <div className="mega-region-header" style={{ borderColor: region.color }}>
                            <i className={`${region.fa_icon} mega-region-fa-icon`} style={{ color: region.color }}></i>
                            <h4 className="mega-region-title" style={{ color: region.color }}>{region.region}</h4>
                          </div>
                          <ul className="mega-dest-list">
                            {(region.destinations || []).map((dest, i) => {
                              const isBold = typeof dest === 'object' && dest.bold;
                              const label = typeof dest === 'object' ? dest.label : dest;
                              return (
                                <li key={i} className={`mega-dest-item ${isBold ? 'mega-dest-bold' : ''}`}>
                                  <button
                                    className="mega-dest-btn"
                                    onClick={() => openItinerary(label)}
                                    title={`View ${label} itinerary`}
                                  >
                                    {label}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Trending chips */}
                    <div className="mega-quick">
                      <span className="mega-quick-label"><i className="fa-solid fa-arrow-trend-up"></i> Trending</span>
                      {['Bali', 'Dubai', 'Maldives', 'Kerala', 'Switzerland', 'Thailand'].map(d => (
                        <button key={d} className="mega-chip" onClick={() => openItinerary(d)}>
                          {d}
                        </button>
                      ))}
                    </div>

                  </div>
                </div>
              </li>
            ) : item.isRoute ? (
              <li key={index} className="navbar-item">
                <button
                  className="navbar-link"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                  onClick={() => navigate(item.href)}
                >
                  <i className={`${item.icon} nav-icon`}></i>
                  <span className="nav-text">{item.name}</span>
                </button>
              </li>
            ) : (
              <li key={index} className="navbar-item">
                <a href={item.href} className="navbar-link">
                  <i className={`${item.icon} nav-icon`}></i>
                  <span className="nav-text">{item.name}</span>
                </a>
              </li>
            )
          )}
        </ul>

        {/* Mobile Toggle */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}>
        <ul className="mobile-menu-list">
          {navItems.map((item, index) =>
            item.hasMega ? (
              <li key={index} className="mobile-menu-item">
                {/* Holidays accordion trigger */}
                <button
                  className={`mobile-menu-link mobile-holidays-trigger ${isMobileHolidaysOpen ? 'open' : ''}`}
                  onClick={() => setIsMobileHolidaysOpen(o => !o)}
                >
                  <i className={`${item.icon} nav-icon-mobile`}></i>
                  <span style={{ flex: 1 }}>{item.name}</span>
                  <i className={`fa-solid fa-chevron-down mobile-holidays-chevron ${isMobileHolidaysOpen ? 'rotated' : ''}`}></i>
                </button>

                {/* Accordion panel */}
                <div className={`mobile-holidays-panel ${isMobileHolidaysOpen ? 'open' : ''}`}>
                  {/* View All banner */}
                  <button
                    className="mobile-view-all-btn"
                    onClick={() => { setIsMobileMenuOpen(false); setIsMobileHolidaysOpen(false); navigate('/packages'); }}
                  >
                    <i className="fa-solid fa-earth-asia"></i> View All Packages
                  </button>

                  {/* Regions only — click goes to View All Packages */}
                  {megaMenuData.length === 0 ? (
                    <div className="mobile-holidays-loading">
                      <i className="fa-solid fa-circle-notch fa-spin"></i> Loading…
                    </div>
                  ) : (
                    <div className="mobile-region-grid">
                      {megaMenuData.map(region => (
                        <button
                          key={region.region}
                          className="mobile-region-chip"
                          style={{ '--rc': region.color }}
                          onClick={() => { setIsMobileMenuOpen(false); setIsMobileHolidaysOpen(false); navigate('/packages'); }}
                        >
                          <i className={region.fa_icon} style={{ color: region.color }}></i>
                          <span>{region.region}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ) : item.isRoute ? (
              <li key={index} className="mobile-menu-item">
                <button
                  className="mobile-menu-link"
                  onClick={() => { setIsMobileMenuOpen(false); navigate(item.href); }}
                >
                  <i className={`${item.icon} nav-icon-mobile`}></i>
                  {item.name}
                </button>
              </li>
            ) : (
              <li key={index} className="mobile-menu-item">
                <a href={item.href} className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>
                  <i className={`${item.icon} nav-icon-mobile`}></i>
                  {item.name}
                </a>
              </li>
            )
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
